import React, { useEffect } from 'react'
import { useRefetchableFragment, graphql } from 'react-relay/hooks'
import { useFormikContext } from 'formik'
import { flatten, pick } from 'lodash'
import { ResolutionAnomalyType, Transaction, DataSection, Column } from '../types'
import { duplicateAnomalies, codingAnomalies } from '../constants'
import ReportTable from '../view/common/ReportTable'
import { reportResolutionColumns, typeOneLineCols } from './constants'
import { useModals } from 'hooks'
import ResolutionModalContainer from './ResolutionModalContainer'

type Props = {
  report: any
  selectedAnomalyType: ResolutionAnomalyType
  resolutionId: string
  onClose: () => boolean
}

const duplicateHeaders = ['Date', 'Number', 'Amount']

// transaction data fields to condition the query
const anomalyTransactionFields: Record<string, string[]> = {
  inconsistentCustomerCoding: ['transactionEntityId'],
  inconsistentVendorCoding: ['transactionEntityId'],
  duplicateBills: ['transactionNum', 'transactionEntityId'],
  duplicateChecks: ['transactionNum', 'transactionAccountId'],
  duplicateInvoices: ['transactionNum'],
}

export default function RelatedTransactionsTable(props: Props) {
  const { report, selectedAnomalyType, resolutionId, onClose } = props
  const { openModal } = useModals()
  const initialValues = useFormikContext().initialValues as Transaction

  const [data, refetch] = useRefetchableFragment(
    graphql`
      fragment RelatedTransactionsTable_report on Report
        @refetchable(queryName: "RelatedTransactionsRefetchQuery")
        @argumentDefinitions(
          anomalies: { type: "[String]", defaultValue: [] }
          transactionData: { type: JSON }
        ) {
        id
        filteredReportResolutions(
          input: { anomalies: $anomalies, transactionData: $transactionData }
        ) {
          nodes {
            id
            currentValue
            initialValue
            anomalyTypes
            status
            resolutionKind
          }
        }
      }
    `,
    report,
  )

  useEffect(() => {
    refetch(
      {
        anomalies: [selectedAnomalyType.type],
        transactionData: pick(initialValues, anomalyTransactionFields[selectedAnomalyType.type]),
      },
      { fetchPolicy: 'store-and-network' },
    )
  }, [selectedAnomalyType, initialValues, refetch])

  // filter out current resolution
  const relatedResolutions = data?.filteredReportResolutions?.nodes?.filter(
    (node: any) => node.id !== resolutionId,
  )

  const relatedDataSection: DataSection = {
    key: 'relatedDataTable',
    tableOptions: {
      tableProps: {
        border: 'solid 1px',
        borderColor: 'background.1',
        boxShadow: 'low',
      },
    },
    onRowClick: (row: any) => {
      // onClose returns true if the the user confirms
      if (onClose()) {
        // @ts-ignore
        openModal(
          ResolutionModalContainer,
          {
            resolutionId: row.original.resolutionId,
            platformCompanyId: row.original.platformCompanyId,
            reportId: data.id,
          },
          'screen',
        )
      }
    },
  }

  if (duplicateAnomalies.includes(selectedAnomalyType.type)) {
    relatedDataSection.columns = reportResolutionColumns.filter((col: Column) =>
      typeof col.Header === 'string' ? duplicateHeaders.includes(col.Header) : false,
    )

    relatedDataSection.data = relatedResolutions?.map((resolution: any) => ({
      ...resolution.currentValue.transactionData,
      // resolution id and platform company id for modal querying
      resolutionId: resolution.id,
      platformCompanyId: resolution.currentValue.platformCompanyId,
    }))

    relatedDataSection.tableOptions!.emptyMessage =
      'There are no longer any duplicates for this transaction.'
  }

  if (codingAnomalies.includes(selectedAnomalyType.type)) {
    relatedDataSection.columns = typeOneLineCols

    // flatten to array of transaction lines
    relatedDataSection.data = flatten(
      relatedResolutions?.map((resolution: any) =>
        resolution.currentValue.transactionData.transactionLines.map((line: any) => ({
          ...line,
          // resolution id and platform company id for modal querying
          resolutionId: resolution.id,
          platformCompanyId: resolution.currentValue.platformCompanyId,
        })),
      ),
    )

    relatedDataSection.tableOptions!.emptyMessage = 'No lines to display.'
  }

  return <ReportTable dataSection={relatedDataSection} />
}
